(() => {
  if (customElements.get('vacuum-finder')) return;
  const requests = new Map();
  const normalize = (value) => value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().replace(/ß/g, 'ss');
  const brandKey = (value) => normalize(value.trim()).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const searchExamples = new Map([
    ["AEG", "AEG Vampyr CE"],
    ["Alaska", "Alaska VC 2000"],
    ["Bestron", "Bestron AS 1500 SB"],
    ["Bomann", "Bomann BS 985 CB"],
    ["Bosch", "Bosch BGL35MON13"],
    ["Clatronic", "Clatronic BS 1225"],
    ["Dirt Devil", "Dirt Devil DD7700"],
    ["EIO", "EIO Vivo"],
    ["Electrolux", "Electrolux Ultrasilencer"],
    ["Emerio", "Emerio VC 101965"],
    ["ETA", "ETA Silent"],
    ["Grundig", "Grundig VCC 3850 A"],
    ["Hanseatic", "Hanseatic Power Clean 3000 HEPA"],
    ["Hoover", "Hoover Telios Plus"],
    ["KHG", "KHG BS 2000 MT"],
    ["Kärcher", "Kärcher WD 5"],
    ["Miele", "Miele Complete C3"],
    ["Nilfisk", "Nilfisk Attix 30"],
    ["Numatic", "Numatic Henry"],
    ["Omega", "Omega Rubin 2000 Parkett"],
    ["Philips", "Philips FC 9197"],
    ["Progress", "Progress PC 3716"],
    ["Quigg", "Quigg Eco2"],
    ["Rowenta", "Rowenta RO 3957"],
    ["Samsung", "Samsung SC 1200"],
    ["Severin", "Severin BR 7937"],
    ["Siemens", "Siemens VSQ5X1230"],
    ["Thomas", "Thomas Premium EL"],
    ["Tristar", "Tristar SZ 2179"]
  ].map(([brand, example]) => [brandKey(brand), example]));

  class VacuumFinder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.events = new AbortController();
      this.data = null;
      this.selectedBrand = '';
      this.brandChanged = false;
      this.selectedModel = '';
      this.resultVersion = 0;
      this.submitButton = this.querySelector('[data-vf-submit]');
      this.status = this.querySelector('[data-vf-status]');
      this.results = this.querySelector('[data-vf-results]');
      this.searchInput = this.querySelector('.vacuum-finder__search input[name="q"]');
      this.defaultSearchPlaceholder ||= this.searchInput.placeholder;
      this.brand = this.makeCombo('brand', () => this.data ? Object.keys(this.data) : [], (value) => {
        this.brandChanged = true;
        this.selectedBrand = value;
        this.selectedModel = '';
        this.model.reset();
        this.model.input.disabled = !value;
        this.model.input.placeholder = value ? this.dataset.modelActive : this.dataset.modelIdle;
        this.updateSelection();
      });
      this.model = this.makeCombo('model', () => (this.data?.[this.selectedBrand] || []).map(([model]) => model), (value) => {
        this.selectedModel = value;
        this.updateSelection();
      });
      this.brand.reset();
      this.model.reset();
      this.model.input.disabled = true;
      this.updateSelection();
      this.on(this.querySelector('[data-vf-form]'), 'submit', (event) => {
        event.preventDefault();
        this.showProducts();
      });
      this.on(document, 'pointerdown', (event) => {
        if (!this.contains(event.target)) {
          this.brand.close();
          this.model.close();
        }
      });
      this.on(this, 'focusin', () => this.load(true));
      this.on(this, 'pointerdown', () => this.load(true));
      const later = () => {
        if ('requestIdleCallback' in window) this.idle = requestIdleCallback(() => this.load(false), { timeout: 2000 });
        else this.timer = setTimeout(() => this.load(false), 200);
      };
      if (this.dataset.collectionHandle || this.dataset.collectionTitle) this.load(true);
      else if (document.readyState === 'complete') later();
      else this.on(window, 'load', later, { once: true });
    }

    disconnectedCallback() {
      this.events?.abort();
      if (this.idle) cancelIdleCallback(this.idle);
      clearTimeout(this.timer);
      this.initialized = false;
    }

    on(target, event, callback, options = {}) {
      target.addEventListener(event, callback, { ...options, signal: this.events.signal });
    }

    makeCombo(name, getItems, onChange) {
      const box = this.querySelector(`[data-vf-combo="${name}"]`);
      const input = box.querySelector('input');
      const list = box.querySelector('[role="listbox"]');
      let selected = '', active = -1, visible = [];
      const close = () => {
        list.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
        active = -1;
      };
      const choose = (value) => {
        selected = value;
        input.value = value;
        close();
        onChange(value);
      };
      const open = () => {
        if (input.disabled) return;
        const query = normalize(input.value === selected ? '' : input.value.trim());
        visible = getItems().filter((value) => normalize(value).includes(query));
        list.replaceChildren();
        active = -1;
        input.removeAttribute('aria-activedescendant');
        if (!visible.length) {
          const empty = document.createElement('li');
          empty.className = 'vf-combo__empty';
          empty.textContent = this.data ? this.dataset.empty : (this.failed ? this.dataset.error : this.dataset.loading);
          list.append(empty);
        }
        const fragment = document.createDocumentFragment();
        visible.forEach((value, index) => {
          const option = document.createElement('li');
          option.id = `${list.id}-${index}`;
          option.setAttribute('role', 'option');
          option.setAttribute('aria-selected', 'false');
          option.className = 'vf-combo__option';
          option.textContent = value;
          // Verhindert Fokusverlust beim Klicken; die Auswahl erfolgt auch auf Touch-Geräten per click.
          option.addEventListener('mousedown', (event) => event.preventDefault());
          option.addEventListener('click', () => choose(value));
          fragment.append(option);
        });
        list.append(fragment);
        list.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      };
      const highlight = (index) => {
        const options = list.querySelectorAll('[role="option"]');
        if (!options.length) return;
        active = Math.max(0, Math.min(index, options.length - 1));
        options.forEach((option, i) => option.setAttribute('aria-selected', String(i === active)));
        input.setAttribute('aria-activedescendant', options[active].id);
        options[active].scrollIntoView({ block: 'nearest' });
      };
      this.on(input, 'focus', open);
      this.on(input, 'click', () => { if (list.hidden) open(); });
      this.on(input, 'input', () => {
        // Auch bei freier Änderung eines bereits gewählten Werts darf kein altes Ziel aktiv bleiben.
        selected = '';
        onChange('');
        open();
      });
      this.on(input, 'keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          if (list.hidden) open();
          highlight(active + (event.key === 'ArrowDown' ? 1 : -1));
        } else if (event.key === 'Enter' && !list.hidden) {
          event.preventDefault();
          if (active >= 0) choose(visible[active]);
          else {
            const exact = visible.find((value) => normalize(value) === normalize(input.value.trim()));
            if (exact) choose(exact);
            else if (visible.length === 1) choose(visible[0]);
          }
        } else if (event.key === 'Escape') {
          event.preventDefault();
          close();
        }
      });
      this.on(box, 'focusout', (event) => {
        if (event.relatedTarget && !box.contains(event.relatedTarget)) close();
      });
      return {
        input, close, choose,
        reset: () => { selected = ''; input.value = ''; close(); },
        refresh: () => { if (!list.hidden) open(); }
      };
    }

    async load(urgent) {
      if (this.data || this.loading || !this.isConnected) return;
      this.loading = true;
      this.failed = false;
      this.setStatus(this.dataset.loading);
      const source = this.dataset.src;
      if (!requests.has(source)) {
        const request = fetch(source, { priority: urgent ? 'high' : 'low' }).then((response) => {
          if (!response.ok) throw new Error('Geräteliste nicht verfügbar');
          return response.json();
        }).then((data) => {
          if (!data || !Object.keys(data).length || Object.values(data).some((models) => !Array.isArray(models) || models.some((row) => !Array.isArray(row) || typeof row[0] !== 'string' || !Array.isArray(row[1]) || row[1].some((h) => typeof h !== 'string' || /[/?#]/.test(h))))) {
            throw new Error('Ungültige Geräteliste');
          }
          return data;
        }).catch((error) => { requests.delete(source); throw error; });
        requests.set(source, request);
      }
      try {
        this.data = await requests.get(source);
        if (!this.brandChanged && (this.dataset.collectionHandle || this.dataset.collectionTitle)) {
          const title = normalize((this.dataset.collectionTitle || '').trim());
          let brands = Object.keys(this.data).filter((brand) => brandKey(brand) === this.dataset.collectionHandle);
          if (!brands.length) brands = Object.keys(this.data).filter((brand) => normalize(brand) === title);
          if (brands.length === 1) this.brand.choose(brands[0]);
        }
        this.setStatus('');
      } catch {
        this.failed = true;
        this.setStatus(this.dataset.error, true);
      } finally {
        this.loading = false;
        this.brand.refresh();
      }
    }

    updateSelection() {
      const example = this.data?.[this.selectedBrand]?.find(([model, handles]) => model && handles.length);
      const collectionBrand = this.dataset.collectionHandle || this.dataset.collectionTitle;
      const brand = this.selectedBrand || (!this.brandChanged ? collectionBrand : '') || '';
      const searchExample = searchExamples.get(brandKey(brand))
        || (example ? `${this.selectedBrand} ${example[0]}` : '');
      this.searchInput.placeholder = searchExample
        ? `${this.dataset.searchExamplePrefix} ${searchExample}`
        : this.defaultSearchPlaceholder;
      this.submitButton.disabled = !this.selectedBrand || !this.selectedModel;
      this.results.replaceChildren();
      this.results.hidden = true;
      this.resultVersion = (this.resultVersion || 0) + 1;
      this.setStatus('');
    }

    async showProducts() {
      const row = this.data?.[this.selectedBrand]?.find(([model]) => model === this.selectedModel);
      if (!row) return;
      const handles = row[1];
      const root = `${this.dataset.root.replace(/\/$/, '')}/`;
      if (!handles.length) {
        const search = this.querySelector('.vacuum-finder__search input[name="q"]');
        search.value = `${this.selectedBrand} ${this.selectedModel}`;
        this.setStatus(this.dataset.unavailable, true);
        return;
      }
      const version = ++this.resultVersion;
      this.setStatus(this.dataset.loadingProducts);
      this.results.replaceChildren();
      this.results.hidden = true;
      const products = await Promise.all(handles.map(async (handle) => {
        try {
          const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`);
          if (!response.ok) return null;
          const product = await response.json();
          return product.title ? { handle, title: product.title } : null;
        } catch { return null; }
      }));
      if (version !== this.resultVersion || !this.isConnected) return;
      if (handles.length === 1 && products[0]) {
        window.location.assign(`${root}products/${encodeURIComponent(products[0].handle)}`);
        return;
      }
      products.filter(Boolean).forEach(({ handle, title }) => {
        const li = document.createElement('li');
        const link = document.createElement('a');
        link.href = `${root}products/${encodeURIComponent(handle)}`;
        link.textContent = title;
        li.append(link);
        this.results.append(li);
      });
      this.results.hidden = !this.results.childElementCount;
      this.setStatus(products.some((p) => !p) ? this.dataset.unavailable : this.dataset.multiple, products.some((p) => !p));
    }

    setStatus(text, error = false) {
      this.status.textContent = text;
      this.status.dataset.state = error ? 'error' : '';
    }
  }
  customElements.define('vacuum-finder', VacuumFinder);
})();
